import { useEffect, useState, useCallback, useRef } from "react";
import { http, fmtEUR } from "@/lib/api";
import { printReportSmart } from "@/lib/kioskMode";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ClipboardList,
  RefreshCw,
  Users,
  AlarmClockOff,
  LockKeyhole,
  RotateCcw,
  CheckCircle2,
  Hourglass,
  ChevronRight,
  FileText,
  FileBarChart2,
  Printer,
  ShoppingBag,
  UtensilsCrossed,
  Bike,
  ScrollText,
  XCircle,
  Percent,
  AlertTriangle,
  Store,
} from "lucide-react";

/**
 * «Διαχείριση Βάρδιας» — simplified Reports menu requested by the
 * shop-floor manager. Lets the manager:
 *
 *   1. Filter shifts by day-range and operator.
 *   2. See per-shift totals (ΑΑ / ΒΒ / Σύνολο, αποδείξεις, μετρητά).
 *   3. Force-close any open shift after the operator clocks out
 *      (cash counted, optional notes).
 *   4. Trigger fiscal X and Z reports straight from the same panel.
 *   5. Reset the global Αρ. Παραγγελίας counter after a Z is taken.
 *
 * Backed by /api/shifts/stats (read) + /api/shifts/{id}/manager-close
 * (force-close) + the existing /api/ftext/xreport, /zreport,
 * /counters/global-order-no/reset endpoints.
 */
const todayISO = () => new Date().toISOString().slice(0, 10);
const isoLocal = (s) => (s ? new Date(s).toLocaleString("el-GR") : "—");
const isoShort = (s) =>
  s
    ? new Date(s).toLocaleString("el-GR", {
        day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
      })
    : "αρχή";

/**
 * Print a per-operator (or all-operators) snapshot sized for a **76 mm**
 * wide thermal printable area. We compute the column count from the
 * actual font metrics so the dot-leaders fill the line edge-to-edge,
 * whatever font size the operator picks.
 *
 *     paper width   76 mm
 *     font size      9 pt   (≈ 3.175 mm tall, ≈ 1.905 mm per char in
 *                            a 60%-width monospace family)
 *     ──────────────────────────────────────────────────────────────
 *     PRINT_WIDTH  ≈ 76 mm / 1.905 mm  →  39 chars
 *
 * Change either constant and the layout re-derives itself.
 */
const PAPER_WIDTH_MM = 76;
const PRINT_FONT_PT = 9;
// Monospaced glyph advance ≈ 60 % of the em-square for Consolas / DejaVu
// Sans Mono / Courier New — measured empirically on Chromium 122. The
// 25.4 / 72 factor converts points → millimetres (1 inch = 25.4 mm,
// 72 pt = 1 inch).
const PRINT_CHAR_MM = (PRINT_FONT_PT * 0.6 * 25.4) / 72;
const PRINT_WIDTH = Math.floor(PAPER_WIDTH_MM / PRINT_CHAR_MM);
const _pad = (text, width = PRINT_WIDTH) => {
  const s = String(text ?? "");
  return s.length >= width ? s.slice(0, width) : s + " ".repeat(width - s.length);
};
const _row = (left, right) => {
  // "left ...... right" — dotted leader fills to PRINT_WIDTH.
  const l = String(left ?? "");
  const r = String(right ?? "");
  const dots = Math.max(1, PRINT_WIDTH - l.length - r.length);
  return l + ".".repeat(dots) + r;
};
const _center = (text) => {
  const s = String(text ?? "");
  if (s.length >= PRINT_WIDTH) return s.slice(0, PRINT_WIDTH);
  const pad = Math.floor((PRINT_WIDTH - s.length) / 2);
  return " ".repeat(pad) + s + " ".repeat(PRINT_WIDTH - pad - s.length);
};
const _sep = (ch = "=") => ch.repeat(PRINT_WIDTH);
const _eur = (n) => `${Number(n || 0).toFixed(2).replace(".", ",")} EUR`;

function _renderSnapshotWindow(shift, report) {
  const totals = report?.totals || { total: 0, aa: 0, bb: 0 };
  const orders = report?.orders || {
    total: 0, total_amount: 0,
    retail: 0, retail_amount: 0,
    restaurant: 0, restaurant_amount: 0,
    delivery: 0, delivery_amount: 0,
  };
  const corrections = report?.corrections || { count: 0, items_deleted: 0, cancellations_count: 0, events: [] };
  const discounts = report?.discounts || { amount: 0, lines: 0 };
  const isOpen = !!report?.shift?.is_open;
  const shiftsInRange = report?.shifts_in_range || null;
  // Build the 76-col text body.
  const lines = [];
  lines.push(_center("** ΑΝΑΦΟΡΑ ΒΑΡΔΙΑΣ " + (isOpen ? "(εις εξέλιξιν)" : "(ΚΛΕΙΣΤΗ)") + " **"));
  lines.push(_sep("="));
  lines.push(_pad(`Χειριστής: ${shift.user_name || "—"}`));
  lines.push(_pad(`Άνοιγμα : ${isoLocal(shift.opened_at)}`));
  lines.push(_pad(`Κλείσιμο: ${shift.closed_at ? isoLocal(shift.closed_at) : "—"}`));
  if (report?.shift?.shifts_count) {
    lines.push(_pad(`Σύνολο βαρδιών στο διάστημα: ${report.shift.shifts_count}`));
  }
  lines.push(_sep("-"));
  lines.push(_center("ΤΑΜΕΙΟ"));
  lines.push(_sep("-"));
  lines.push(_row("Μετρητά Αρχής Βάρδιας", _eur(totals.cash_start || 0)));
  lines.push(_row("Ταμείο ΑΑ", _eur(totals.aa)));
  // Per-method AA breakdown: only methods that actually saw money
  // appear here (zero entries are stripped by the backend).
  (totals.aa_breakdown || []).forEach((b) => {
    lines.push(_row(`  · ${b.method_name}`, _eur(b.amount)));
  });
  lines.push(_row("Ταμείο ΒΒ", _eur(totals.bb)));
  lines.push(_row("ΣΥΝΟΛΟ", _eur(totals.total)));
  lines.push(_row("Μετρητά στο Συρτάρι", _eur(totals.cash_drawer || 0)));
  lines.push("");
  lines.push(_sep("-"));
  lines.push(_center("ΠΑΡΑΓΓΕΛΙΕΣ"));
  lines.push(_sep("-"));
  lines.push(_row(`Σύνολο  (${orders.total} παρ.)`, _eur(orders.total_amount)));
  lines.push(_row(`  Retail   (${orders.retail} παρ.)`, _eur(orders.retail_amount)));
  lines.push(_row(`  Εστίαση  (${orders.restaurant} παρ.)`, _eur(orders.restaurant_amount)));
  lines.push(_row(`  Delivery (${orders.delivery} παρ.)`, _eur(orders.delivery_amount)));
  lines.push("");
  lines.push(_sep("-"));
  lines.push(_center("ΔΙΟΡΘΩΣΕΙΣ & ΑΚΥΡΩΣΕΙΣ"));
  lines.push(_sep("-"));
  lines.push(_row("Διορθώσεις παραγγελιών", String(corrections.count)));
  lines.push(_row("Διαγραμμένα είδη", String(corrections.items_deleted)));
  lines.push(_row("Ακυρώσεις αποδείξεων", String(corrections.cancellations_count)));
  if (corrections.events?.length) {
    lines.push("");
    lines.push(_pad("Αναλυτικά:"));
    corrections.events.slice(0, 25).forEach((e) => {
      const head = `  ${isoLocal(e.timestamp)} ${e.target_name || ""}`;
      lines.push(_row(head.slice(0, PRINT_WIDTH - 12), `${e.deleted} είδ.`));
    });
  }
  lines.push("");
  lines.push(_sep("-"));
  lines.push(_center("ΕΚΠΤΩΣΕΙΣ"));
  lines.push(_sep("-"));
  lines.push(_row("Σύνολο εκπτώσεων", _eur(discounts.amount)));
  lines.push(_row("Γραμμές με έκπτωση", String(discounts.lines)));
  if (shiftsInRange?.length) {
    lines.push("");
    lines.push(_sep("-"));
    lines.push(_center("ΑΝΑΛΥΣΗ ΑΝΑ ΒΑΡΔΙΑ"));
    lines.push(_sep("-"));
    shiftsInRange.slice(0, 60).forEach((s) => {
      const who = (s.user_name || "?").slice(0, 22);
      lines.push(_row(`${who} ${isoLocal(s.opened_at)}`.slice(0, PRINT_WIDTH - 12), _eur(s.sales_total)));
    });
  }
  lines.push("");
  lines.push(_sep("="));
  lines.push(_center(`Εκτύπωση: ${new Date().toLocaleString("el-GR")}`));
  lines.push(_sep("="));

  const safeText = lines.join("\n");
  // The print body is a `<pre>` so the browser preserves whitespace; we
  // also set CSS so the page width matches 76 monospace chars at 11pt
  // (≈ standard thermal paper). Auto-trigger window.print() on load.
  const html = `<!doctype html><html lang="el"><head><meta charset="utf-8" />
<title>Αναφορά Βάρδιας — ${(shift.user_name || "").replace(/</g, "&lt;")}</title>
<style>
  @page { size: ${PAPER_WIDTH_MM}mm auto; margin: 0; }
  html, body { margin: 0; padding: 0; background:#fff; }
  pre { font-family: "Consolas", "DejaVu Sans Mono", "Courier New", monospace;
        font-size: ${PRINT_FONT_PT}pt; line-height: 1.25; white-space: pre;
        margin: 0; padding: 2mm; width: ${PAPER_WIDTH_MM}mm;
        box-sizing: border-box; color:#000; }
  @media print { pre { margin: 0; } }
</style></head><body><pre>${safeText
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")}</pre>
</body></html>`;
  // Iframe-based print — see lib/proforma.js for the full rationale.
  // window.open() gets silently blocked on production HTTPS after any
  // preceding ``await http.get(...)``, which was the bug behind
  // «δεν μπορώ να τυπώσω την αναφορά χειριστών». The iframe is
  // same-origin and always allowed to invoke ``.print()``.
  // Companion-first: αν τρέχει τοπικός agent, στέλνουμε το safeText
  // απευθείας για silent ESC/POS printing — δεν ανοίγει καμία μπάρα
  // dialog που να «παγώνει» το POS.
  printReportSmart({ text: safeText, html, afterMs: 250 });
}

export default function ShiftReportsPanel() {
  // Role check — close-shift, reset and the aggregate report are
  // manager-only. We read once on mount; if the user logs out & back in
  // the parent route re-mounts us so this stays fresh.
  const me = (() => {
    try { return JSON.parse(localStorage.getItem("auth_user") || "{}"); } catch { return {}; }
  })();
  const isManager = ["manager", "admin", "super_manager"].includes(me?.role);

  const [dateFrom, setDateFrom] = useState(todayISO());
  const [dateTo, setDateTo] = useState(todayISO());
  const [userId, setUserId] = useState("all");
  const [users, setUsers] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [summary, setSummary] = useState({ shifts_count: 0, open_count: 0, day_total: 0, day_aa: 0, day_bb: 0 });
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [closeDlg, setCloseDlg] = useState({ open: false, shift: null, cash_counted: "", notes: "" });
  const [resetDlg, setResetDlg] = useState(false);
  // Ιούν 2026: «Κλείσιμο Μαγαζιού» + φίλτρο βάρδιας μαγαζιού.
  const [storeDlg, setStoreDlg] = useState({ open: false, admin_code: "", reset_counter: true });
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState("dates");
  const initDefault = useRef(false);
  // Per-operator drill-down («κλικ στον χειριστή»). Holds the loaded
  // /shifts/{id}/operator-report response.
  const [detailShift, setDetailShift] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (sessionId !== "dates") {
        const s = sessions.find((x) => x.id === sessionId);
        if (s?.from) params.ts_from = s.from;
        if (s?.to) params.ts_to = s.to;
        if (!s?.from && !s?.to) params.ts_from = "1970-01-01T00:00:00+00:00";
      } else {
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
      }
      if (userId && userId !== "all") params.user_id = userId;
      const r = await http.get("/shifts/stats", { params });
      setShifts(r.data?.shifts || []);
      setSummary(r.data?.summary || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Αποτυχία φόρτωσης βαρδιών");
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, userId, sessionId, sessions]);

  const loadSessions = useCallback(async () => {
    try {
      const r = await http.get("/shifts/store-sessions");
      const list = r.data?.sessions || [];
      setSessions(list);
      // Προεπιλογή «Τρέχουσα βάρδια» στο πρώτο φόρτωμα (αν υπάρχει ανοιχτή
      // βάρδια μαγαζιού). Δεν επηρεάζει μετέπειτα χειροκίνητες επιλογές.
      if (!initDefault.current) {
        initDefault.current = true;
        if (list.some((s) => s.id === "current")) {
          setSessionId("current");
        }
      }
    } catch {
      setSessions([]);
    }
  }, []);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    // Operator filter — managers only (cashiers see only their own shifts
    // anyway, so a per-user dropdown is irrelevant for them).
    http.get("/users")
      .then((r) => setUsers(r.data || []))
      .catch(() => setUsers([]));
  }, []);

  const submitClose = async () => {
    if (!closeDlg.shift) return;
    setBusy(true);
    try {
      const payload = {
        cash_counted: closeDlg.cash_counted !== "" ? Number(closeDlg.cash_counted) : null,
        notes: closeDlg.notes || null,
      };
      const r = await http.post(`/shifts/${closeDlg.shift.id}/manager-close`, payload);
      const diff = r.data?.diff;
      toast.success(
        `Έκλεισε η βάρδια του «${closeDlg.shift.user_name}»` +
          (diff !== null && diff !== undefined ? ` · διαφορά ${diff >= 0 ? "+" : ""}${diff.toFixed(2)}€` : ""),
      );
      setCloseDlg({ open: false, shift: null, cash_counted: "", notes: "" });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Αποτυχία κλεισίματος");
    } finally {
      setBusy(false);
    }
  };

  const openDetail = async (sh) => {
    setDetailShift(sh);
    setDetailData(null);
    setDetailLoading(true);
    try {
      const r = await http.get(`/shifts/${sh.id}/operator-report`);
      setDetailData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Αποτυχία φόρτωσης αναφοράς");
      setDetailShift(null);
    } finally {
      setDetailLoading(false);
    }
  };

  // Print just the snapshot — used by both the inline row icon (so the
  // manager can hit "X" without first opening the dialog) and by the
  // OperatorReportDialog footer button. Fetches /operator-report fresh
  // every time so the printout is up-to-date even if the table view
  // was loaded a while ago.
  const printSnapshotForShift = async (sh) => {
    setBusy(true);
    try {
      const r = await http.get(`/shifts/${sh.id}/operator-report`);
      _renderSnapshotWindow(sh, r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Αποτυχία εκτύπωσης");
    } finally {
      setBusy(false);
    }
  };

  // «Ενεργοί χειριστές» — quick aggregate roll-up across every
  // currently-open shift, irrespective of the date filter. The button
  // lives next to Ανανέωση so a manager can pull this snapshot at any
  // point in the day without first having to pick a date range.
  const printActiveOperators = async () => {
    setBusy(true);
    try {
      const r = await http.get("/shifts/aggregate-report", { params: { open_only: true } });
      _renderSnapshotWindow(
        {
          user_name: "ΕΝΕΡΓΟΙ ΧΕΙΡΙΣΤΕΣ",
          opened_at: new Date().toISOString(),
          closed_at: null,
        },
        r.data,
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Αποτυχία αναφοράς");
    } finally {
      setBusy(false);
    }
  };

  // «Όλοι οι χειριστές — Σύνολο Ημέρας». Manager-only aggregate report
  // across every shift in the date range. We send the aggregate header
  // synthetically because there is no real shift_id to point to.
  const printAllOperators = async () => {
    setBusy(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const r = await http.get("/shifts/aggregate-report", { params });
      _renderSnapshotWindow(
        {
          user_name: "ΟΛΟΙ ΟΙ ΧΕΙΡΙΣΤΕΣ",
          opened_at: dateFrom ? `${dateFrom}T00:00:00` : null,
          closed_at: dateTo ? `${dateTo}T23:59:59` : null,
        },
        r.data,
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Αποτυχία αναφοράς");
    } finally {
      setBusy(false);
    }
  };

  const submitStoreClose = async () => {
    if (!storeDlg.admin_code.trim()) {
      toast.error("Δώσε τον κωδικό διαχειριστή");
      return;
    }
    setBusy(true);
    try {
      const r = await http.post("/shifts/store-close", {
        admin_code: storeDlg.admin_code.trim(),
        reset_order_counter: !!storeDlg.reset_counter,
      });
      const sc = r.data?.store_close || {};
      if (r.data?.report) {
        _renderSnapshotWindow(
          {
            user_name: "ΚΛΕΙΣΙΜΟ ΜΑΓΑΖΙΟΥ",
            opened_at: sc.since,
            closed_at: sc.closed_at,
          },
          r.data.report,
        );
      }
      toast.success(
        `Το μαγαζί έκλεισε ✓ — ${sc.shifts_closed || 0} βάρδιες έκλεισαν` +
          (sc.reset_order_counter ? " · μηδενίστηκε ο Αρ. Παραγγελίας" : ""),
      );
      setStoreDlg({ open: false, admin_code: "", reset_counter: true });
      await Promise.all([load(), loadSessions()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Αποτυχία κλεισίματος μαγαζιού");
    } finally {
      setBusy(false);
    }
  };

  const submitReset = async () => {
    setBusy(true);
    try {
      await http.post("/counters/global-order-no/reset");
      toast.success("Μηδενίστηκε ο Αρ. Παραγγελίας ✓");
      setResetDlg(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Αποτυχία μηδενισμού");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-5 space-y-4" data-testid="shift-reports-panel">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <ClipboardList size={20} className="text-indigo-600" />
          <h3 className="font-heading text-lg font-bold">Διαχείριση Βάρδιας & Αναφορές Ημέρας</h3>
        </div>
        <div className="flex items-center gap-2">
          {isManager && (
            <Button
              size="sm"
              onClick={() => setStoreDlg({ open: true, admin_code: "", reset_counter: true })}
              disabled={busy || loading}
              className="h-8 bg-red-600 hover:bg-red-700 text-white font-bold border-2 border-red-700"
              data-testid="store-close-btn"
            >
              <Store size={13} className="mr-1" />
              ΚΛΕΙΣΙΜΟ ΜΑΓΑΖΙΟΥ
            </Button>
          )}
          {isManager && (
            <Button
              size="sm"
              onClick={printActiveOperators}
              disabled={busy || loading}
              className="h-8 bg-indigo-600 hover:bg-indigo-700 text-white font-bold border-2 border-indigo-700"
              data-testid="shifts-print-active"
            >
              <Users size={13} className="mr-1" />
              Ενεργοί χειριστές
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            disabled={loading}
            className="h-8 border-2"
            data-testid="shifts-refresh"
          >
            <RefreshCw size={13} className={`mr-1 ${loading ? "animate-spin" : ""}`} />
            Ανανέωση
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 pb-3 border-b border-slate-200">
        <div>
          <Label className="text-xs flex items-center gap-1">
            <Store size={11} /> Βάρδια Μαγαζιού
          </Label>
          <Select
            value={sessionId}
            onValueChange={(v) => setSessionId(v)}
          >
            <SelectTrigger className="h-9 w-72" data-testid="shifts-session-filter">
              <SelectValue placeholder="Ημερολόγιο" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="dates">— Ημερολόγιο (Από/Έως) —</SelectItem>
              {sessions.map((s) =>
                s.id === "current" ? (
                  <SelectItem key="current" value="current">
                    Τρέχουσα βάρδια (άνοιγμα: {isoShort(s.from)})
                  </SelectItem>
                ) : (
                  <SelectItem key={s.id} value={s.id}>
                    {isoShort(s.from)} → {isoShort(s.to)}
                    {s.total !== undefined ? ` · ${fmtEUR(s.total)}` : ""}
                  </SelectItem>
                ),
              )}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Από</Label>
          <Input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="h-9 w-40"
            disabled={sessionId !== "dates"}
            data-testid="shifts-from"
          />
        </div>
        <div>
          <Label className="text-xs">Έως</Label>
          <Input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="h-9 w-40"
            disabled={sessionId !== "dates"}
            data-testid="shifts-to"
          />
        </div>
        <div>
          <Label className="text-xs flex items-center gap-1">
            <Users size={11} /> Χειριστής
          </Label>
          <Select value={userId} onValueChange={setUserId}>
            <SelectTrigger className="h-9 w-56" data-testid="shifts-user-filter">
              <SelectValue placeholder="Όλοι" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Όλοι οι χειριστές</SelectItem>
              {users.map((u) => (
                <SelectItem key={u.id} value={u.id}>
                  {u.name} <span className="text-slate-400 text-xs">— {u.role}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          onClick={() => { setSessionId("dates"); setDateFrom(todayISO()); setDateTo(todayISO()); setUserId("all"); }}
          variant="ghost"
          size="sm"
          className="h-9"
          data-testid="shifts-quick-today"
        >
          Σήμερα
        </Button>
      </div>

      {/* Day totals (read-only summary — fiscal X/Z buttons live further
          down in the Reports page; this section is strictly per-operator
          shift management, no cash-register actions on purpose). */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile label="Βάρδιες" value={summary.shifts_count} sub={`${summary.open_count} ανοιχτές`} color="slate" />
        <Tile label="Σύνολο" value={fmtEUR(summary.day_total)} color="indigo" />
        <Tile label="Ταμείο ΑΑ" value={fmtEUR(summary.day_aa)} color="emerald" />
        <Tile label="Ταμείο ΒΒ" value={fmtEUR(summary.day_bb)} color="amber" />
      </div>

      {/* Feb 2026 UX: stale-shift alert. Any shift open >24h is a red flag
          — typically ο χειριστής ξέχασε να πατήσει «Κλείσιμο Βάρδιας» πριν
          φύγει. Δείχνουμε alert banner + bulk-close κουμπί (manager only). */}
      {isManager && (summary.stale_shifts_count || 0) > 0 && (
        <div
          className="flex items-start gap-3 p-3 rounded-md border-2 border-amber-300 bg-amber-50"
          data-testid="stale-shifts-alert"
        >
          <AlertTriangle size={20} className="text-amber-700 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-bold text-amber-900 text-sm">
              {summary.stale_shifts_count} {summary.stale_shifts_count === 1 ? "βάρδια" : "βάρδιες"} ανοιχτ{summary.stale_shifts_count === 1 ? "ή" : "ές"} πάνω από 24 ώρες
            </div>
            <div className="text-xs text-amber-800 mt-0.5">
              Αν κάποιος χειριστής ξέχασε να κλείσει τη βάρδιά του, το «Κλείσε παλιές βάρδιες» θα τις κλείσει όλες αυτόματα με audit trail.
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="border-amber-500 text-amber-800 hover:bg-amber-100 font-bold shrink-0"
            disabled={busy}
            onClick={async () => {
              // eslint-disable-next-line no-alert
              if (!window.confirm(`Θα κλείσουν αυτόματα ${summary.stale_shifts_count} βάρδιες που είναι ανοιχτές >24h. Συνέχεια;`)) return;
              setBusy(true);
              try {
                const r = await http.post("/shifts/close-stale");
                toast.success(`Έκλεισαν ${r.data?.closed || 0} παλιές βάρδιες`);
                await load();
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Αποτυχία bulk-close");
              } finally {
                setBusy(false);
              }
            }}
            data-testid="stale-shifts-bulk-close"
          >
            Κλείσε παλιές βάρδιες
          </Button>
        </div>
      )}

      {/* Big «Όλοι οι Χειριστές» button — manager-only.
          Prints the day-roll-up across every shift in the date range. */}
      {isManager && (
        <Button
          onClick={printAllOperators}
          disabled={busy || loading}
          className="w-full h-14 bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white font-bold text-base shadow-md"
          data-testid="shifts-print-all"
        >
          <Users size={18} className="mr-2" />
          ΟΛΟΙ ΟΙ ΧΕΙΡΙΣΤΕΣ — Εκτύπωση Συνόλου Ημέρας
        </Button>
      )}

      {/* Shifts table */}
      <div className="overflow-x-auto border border-slate-200 rounded-md">
        <table className="w-full text-sm" data-testid="shifts-table">
          <thead className="bg-slate-100 text-slate-700">
            <tr className="text-left">
              <th className="px-3 py-2 font-bold">Χειριστής</th>
              <th className="px-3 py-2 font-bold">Άνοιγμα</th>
              <th className="px-3 py-2 font-bold">Κλείσιμο</th>
              <th className="px-3 py-2 font-bold text-right">Αρχικό ταμείο</th>
              <th className="px-3 py-2 font-bold text-right">Σύνολο</th>
              <th className="px-3 py-2 font-bold text-right">ΑΑ</th>
              <th className="px-3 py-2 font-bold text-right">ΒΒ</th>
              <th className="px-3 py-2 font-bold text-right">Αποδ.</th>
              <th className="px-3 py-2 font-bold text-center">Ενέργειες</th>
            </tr>
          </thead>
          <tbody>
            {shifts.length === 0 && !loading && (
              <tr>
                <td colSpan={9} className="text-center text-slate-400 py-6">
                  Δεν βρέθηκαν βάρδιες για το επιλεγμένο διάστημα
                </td>
              </tr>
            )}
            {shifts.map((sh) => (
              <tr
                key={sh.id}
                className="border-t border-slate-100 hover:bg-indigo-50 cursor-pointer"
                onClick={() => openDetail(sh)}
                data-testid={`shift-row-${sh.id}`}
              >
                <td className="px-3 py-2 font-medium">
                  {sh.user_name || sh.user_id}
                  {sh.is_open && (
                    <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-100 border border-emerald-300 px-1.5 py-0.5 rounded-full">
                      <Hourglass size={9} /> ΑΝΟΙΧΤΗ
                    </span>
                  )}
                  {!sh.is_open && (
                    <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold text-slate-600 bg-slate-200 px-1.5 py-0.5 rounded-full">
                      <CheckCircle2 size={9} /> ΚΛΕΙΣΤΗ
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-xs text-slate-700">{isoLocal(sh.opened_at)}</td>
                <td className="px-3 py-2 text-xs text-slate-700">{isoLocal(sh.closed_at)}</td>
                <td className="px-3 py-2 text-right font-mono">{fmtEUR(sh.cash_start)}</td>
                <td className="px-3 py-2 text-right font-mono font-bold">{fmtEUR(sh.sales_total)}</td>
                <td className="px-3 py-2 text-right font-mono text-emerald-700">{fmtEUR(sh.sales_aa)}</td>
                <td className="px-3 py-2 text-right font-mono text-amber-700">{fmtEUR(sh.sales_bb)}</td>
                <td className="px-3 py-2 text-right">{sh.receipts_count || 0}</td>
                <td className="px-3 py-2">
                  {/* Larger, labeled action buttons — kitchen staff
                      typically uses gloved hands, icons-only proved too
                      easy to mis-tap. stopPropagation so the row-click
                      drill-down doesn't fire when the manager goes
                      straight to print or close. */}
                  <div className="flex items-center justify-end gap-2 flex-wrap">
                    <Button
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); openDetail(sh); }}
                      className="h-10 px-3 bg-slate-100 hover:bg-slate-200 text-slate-800 font-bold border border-slate-300"
                      data-testid={`shift-view-${sh.id}`}
                    >
                      <ScrollText size={15} className="mr-1.5" />
                      Αναφορά
                    </Button>
                    <Button
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); printSnapshotForShift(sh); }}
                      disabled={busy}
                      className="h-10 px-3 bg-blue-600 hover:bg-blue-700 text-white font-bold"
                      data-testid={`shift-print-${sh.id}`}
                    >
                      <Printer size={15} className="mr-1.5" />
                      Εκτύπωση X
                    </Button>
                    {sh.is_open && isManager && (
                      <Button
                        size="sm"
                        onClick={async (e) => {
                          e.stopPropagation();
                          await printSnapshotForShift(sh);
                          setCloseDlg({ open: true, shift: sh, cash_counted: "", notes: "" });
                        }}
                        disabled={busy}
                        className="h-10 px-3 bg-red-600 hover:bg-red-700 text-white font-bold"
                        data-testid={`shift-close-${sh.id}`}
                      >
                        <AlarmClockOff size={15} className="mr-1.5" />
                        Κλείσιμο Z
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Reset counter section — manager-only */}
      {isManager && (
      <div className="pt-2 border-t border-dashed border-slate-300 flex items-center justify-between flex-wrap gap-3">
        <div className="text-xs text-slate-500 max-w-xl">
          Μετά την έκδοση Z για όλες τις ταμειακές, μπορείς να μηδενίσεις τον
          μετρητή Αρ. Παραγγελίας ώστε η επόμενη ημέρα να ξεκινήσει από 1.
          Επιτρέπεται μόνο όταν έχουν κλείσει όλες οι βάρδιες.
        </div>
        <Button
          variant="outline"
          onClick={() => setResetDlg(true)}
          disabled={busy || summary.open_count > 0}
          className="border-2 border-amber-500 text-amber-700 hover:bg-amber-50 font-bold"
          data-testid="shifts-reset-counters"
        >
          <RotateCcw size={14} className="mr-1.5" />
          Μηδενισμός Αρ. Παραγγελίας
        </Button>
      </div>
      )}

      {/* Per-operator drill-down («κλικ στον χειριστή»). Shows AA/BB,
          breakdown by module, corrections/cancellations of the shift,
          discounts, plus per-shift "X" (snapshot print, no reset) and
          "Z" (snapshot print + close shift, zero counters). NOT linked
          to the fiscal cash register — those buttons live elsewhere. */}
      <OperatorReportDialog
        open={!!detailShift}
        onClose={() => { setDetailShift(null); setDetailData(null); }}
        shift={detailShift}
        report={detailData}
        loading={detailLoading}
        canCloseShift={isManager}
        onCloseShift={() => {
          if (detailShift) {
            setCloseDlg({ open: true, shift: detailShift, cash_counted: "", notes: "" });
            setDetailShift(null);
            setDetailData(null);
          }
        }}
      />

      {/* Store close dialog (Ιούν 2026) */}
      <Dialog open={storeDlg.open} onOpenChange={(o) => !o && setStoreDlg({ open: false, admin_code: "", reset_counter: true })}>
        <DialogContent className="max-w-md" data-testid="store-close-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Store size={18} /> ΚΛΕΙΣΙΜΟ ΜΑΓΑΖΙΟΥ
            </DialogTitle>
            <DialogDescription>
              Θα εκτυπωθεί αναφορά όλης της βάρδιας για όλους τους χειριστές,
              θα κλείσουν ΟΛΕΣ οι ανοιχτές βάρδιες και το ταμείο θα ξεκινήσει
              από το 0 στη νέα βάρδια.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Κωδικός Manager</Label>
              {/* Inline numpad — ίδιο μοτίβο με το Force Reset, ώστε το
                  numpad να είναι πάντα ορατό μέσα στο dialog (αποφεύγει
                  το Radix outside-click bug του floating Numpad). */}
              <div
                className="mt-1 rounded-lg border-2 border-slate-300 bg-slate-50 p-3 text-center font-mono text-3xl tracking-[0.4em] text-slate-800 select-none min-h-[52px] flex items-center justify-center"
                data-testid="store-close-code-display"
              >
                {storeDlg.admin_code
                  ? "•".repeat(storeDlg.admin_code.length)
                  : <span className="text-slate-400 text-lg tracking-normal">Πληκτρολογήστε κωδικό</span>}
              </div>
              <div className="grid grid-cols-3 gap-2 mt-2" data-testid="store-close-numpad">
                {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((k) => (
                  <button
                    type="button"
                    key={k}
                    onClick={() => setStoreDlg((d) => ({ ...d, admin_code: (d.admin_code + k).slice(0, 20) }))}
                    data-testid={`store-close-key-${k}`}
                    className="h-14 rounded-lg border-2 border-slate-300 bg-white hover:bg-slate-100 active:scale-95 active:bg-slate-200 text-2xl font-bold text-slate-800 transition-all shadow-sm"
                  >
                    {k}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setStoreDlg((d) => ({ ...d, admin_code: "" }))}
                  data-testid="store-close-key-clear"
                  className="h-14 rounded-lg border-2 border-amber-300 bg-amber-50 hover:bg-amber-100 active:scale-95 text-sm font-bold text-amber-800 transition-all shadow-sm"
                >
                  ΚΑΘΑΡ.
                </button>
                <button
                  type="button"
                  onClick={() => setStoreDlg((d) => ({ ...d, admin_code: (d.admin_code + "0").slice(0, 20) }))}
                  data-testid="store-close-key-0"
                  className="h-14 rounded-lg border-2 border-slate-300 bg-white hover:bg-slate-100 active:scale-95 active:bg-slate-200 text-2xl font-bold text-slate-800 transition-all shadow-sm"
                >
                  0
                </button>
                <button
                  type="button"
                  onClick={() => setStoreDlg((d) => ({ ...d, admin_code: d.admin_code.slice(0, -1) }))}
                  data-testid="store-close-key-backspace"
                  className="h-14 rounded-lg border-2 border-slate-300 bg-slate-100 hover:bg-slate-200 active:scale-95 text-lg font-bold text-slate-700 transition-all shadow-sm"
                >
                  ⌫
                </button>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm font-medium cursor-pointer select-none bg-amber-50 border border-amber-200 rounded-md p-3">
              <input
                type="checkbox"
                checked={!!storeDlg.reset_counter}
                onChange={(e) => setStoreDlg({ ...storeDlg, reset_counter: e.target.checked })}
                className="w-4 h-4 accent-amber-600"
                data-testid="store-close-reset-counter"
              />
              Μηδενισμός μετρητών παραγγελιών (Αρ. Παραγγελίας → 1)
            </label>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setStoreDlg({ open: false, admin_code: "", reset_counter: true })}
              disabled={busy}
              data-testid="store-close-cancel"
            >
              Ακύρωση
            </Button>
            <Button
              onClick={submitStoreClose}
              disabled={busy || !storeDlg.admin_code.trim()}
              className="bg-red-600 hover:bg-red-700 text-white font-bold"
              data-testid="store-close-confirm"
            >
              <Store size={14} className="mr-1.5" />
              {busy ? "Κλείσιμο..." : "Κλείσιμο & Εκτύπωση"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Close shift dialog */}
      <Dialog open={closeDlg.open} onOpenChange={(o) => !o && setCloseDlg({ open: false, shift: null, cash_counted: "", notes: "" })}>
        <DialogContent className="max-w-md" data-testid="shift-close-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LockKeyhole size={18} /> Κλείσιμο Βάρδιας
            </DialogTitle>
            <DialogDescription>
              {closeDlg.shift && (
                <>Χειριστής: <b>{closeDlg.shift.user_name}</b> — Άνοιγμα: {isoLocal(closeDlg.shift.opened_at)}</>
              )}
            </DialogDescription>
          </DialogHeader>
          {closeDlg.shift && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2 text-sm bg-slate-50 p-3 rounded-md">
                <div className="text-slate-500">Αρχικό ταμείο</div>
                <div className="font-mono font-bold text-right">{fmtEUR(closeDlg.shift.cash_start)}</div>
                <div className="text-slate-500">Πωλήσεις ΑΑ</div>
                <div className="font-mono font-bold text-right text-emerald-700">{fmtEUR(closeDlg.shift.sales_aa)}</div>
                <div className="text-slate-500">Αναμενόμενα μετρητά</div>
                <div className="font-mono font-bold text-right">
                  {fmtEUR((closeDlg.shift.cash_start || 0) + (closeDlg.shift.sales_aa || 0))}
                </div>
              </div>
              <div>
                <Label className="text-xs">Μετρημένα μετρητά (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={closeDlg.cash_counted}
                  onChange={(e) => setCloseDlg({ ...closeDlg, cash_counted: e.target.value })}
                  placeholder="πχ. 124.50"
                  className="h-10 text-lg font-mono"
                  data-testid="shift-close-cash-counted"
                />
              </div>
              <div>
                <Label className="text-xs">Σημείωση (προαιρετικά)</Label>
                <Input
                  value={closeDlg.notes}
                  onChange={(e) => setCloseDlg({ ...closeDlg, notes: e.target.value })}
                  placeholder="πχ. ο χειριστής έφυγε νωρίτερα"
                  className="h-10"
                  data-testid="shift-close-notes"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCloseDlg({ open: false, shift: null, cash_counted: "", notes: "" })}
              disabled={busy}
              data-testid="shift-close-cancel"
            >
              Ακύρωση
            </Button>
            <Button
              onClick={submitClose}
              disabled={busy}
              className="bg-red-600 hover:bg-red-700 text-white font-bold"
              data-testid="shift-close-confirm"
            >
              <AlarmClockOff size={14} className="mr-1.5" />
              Κλείσιμο Βάρδιας
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset counter confirm dialog */}
      <Dialog open={resetDlg} onOpenChange={setResetDlg}>
        <DialogContent className="max-w-md" data-testid="reset-counter-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <RotateCcw size={18} /> Μηδενισμός Αρ. Παραγγελίας
            </DialogTitle>
            <DialogDescription>
              Θα μηδενιστεί ο γενικός μετρητής σειριακών αριθμών παραγγελίας.
              Βεβαιώσου ότι έχεις πάρει Z από όλες τις ταμειακές πρώτα.
              Δεν αντιστρέφεται.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setResetDlg(false)}
              disabled={busy}
              data-testid="reset-counter-cancel"
            >
              Ακύρωση
            </Button>
            <Button
              onClick={submitReset}
              disabled={busy}
              className="bg-amber-600 hover:bg-amber-700 text-white font-bold"
              data-testid="reset-counter-confirm"
            >
              Μηδενισμός
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function Tile({ label, value, sub, color = "slate" }) {
  const colors = {
    slate: "border-slate-300 bg-slate-50 text-slate-800",
    indigo: "border-indigo-300 bg-indigo-50 text-indigo-700",
    emerald: "border-emerald-300 bg-emerald-50 text-emerald-700",
    amber: "border-amber-300 bg-amber-50 text-amber-700",
    red: "border-red-300 bg-red-50 text-red-700",
    sky: "border-sky-300 bg-sky-50 text-sky-700",
    fuchsia: "border-fuchsia-300 bg-fuchsia-50 text-fuchsia-700",
  };
  return (
    <div className={`rounded-lg border-2 px-3 py-2 ${colors[color]}`}>
      <div className="text-[10px] uppercase tracking-wider font-bold opacity-75">{label}</div>
      <div className="text-xl font-bold font-mono mt-0.5">{value}</div>
      {sub && <div className="text-[10px] opacity-70 mt-0.5">{sub}</div>}
    </div>
  );
}


/**
 * OperatorReportDialog — opens when the manager clicks a shift row.
 *
 * Renders the «αναφορά εις εξέλιξιν» (running snapshot) requested by the
 * shop owner: Ταμείο ΑΑ, Ταμείο ΒΒ, σύνολο, παραγγελίες ανά module
 * (Retail / Εστίαση / Delivery), διορθώσεις-διαγραφές, ακυρώσεις και
 * συνολικές εκπτώσεις.
 *
 * Two action buttons mirror the per-operator "X" and "Z" reports — but
 * they're STRICTLY for this operator's shift; they don't touch the
 * fiscal cash register. The cash-register X/Z buttons live in a
 * different section further down in the Reports page.
 *
 *   • X = print snapshot, leave the shift open ("εις εξέλιξιν").
 *   • Z = print snapshot, then ask the manager to close the shift
 *     (cash counting flow), thereby zeroing the operator's totals.
 */
function OperatorReportDialog({ open, onClose, shift, report, loading, canCloseShift = false, onCloseShift }) {
  if (!shift) return null;
  const isOpen = !!report?.shift?.is_open;
  const totals = report?.totals || { total: 0, aa: 0, bb: 0 };
  const orders = report?.orders || { total: 0, retail: 0, restaurant: 0, delivery: 0 };
  const corrections = report?.corrections || { count: 0, items_deleted: 0, cancellations_count: 0, events: [], cancellation_events: [] };
  const discounts = report?.discounts || { amount: 0, lines: 0 };

  const printSnapshot = () => _renderSnapshotWindow(shift, report);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl w-[95vw] max-h-[90vh] overflow-y-auto" data-testid="operator-report-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold flex items-center gap-2">
            <ScrollText size={20} className="text-indigo-600" />
            Αναφορά Βάρδιας — {shift.user_name}
            {isOpen ? (
              <span className="ml-2 text-[11px] inline-flex items-center gap-1 font-bold text-emerald-700 bg-emerald-100 border border-emerald-300 px-2 py-0.5 rounded-full">
                <Hourglass size={10} /> ΕΙΣ ΕΞΕΛΙΞΙΝ
              </span>
            ) : (
              <span className="ml-2 text-[11px] inline-flex items-center gap-1 font-bold text-slate-600 bg-slate-200 px-2 py-0.5 rounded-full">
                <CheckCircle2 size={10} /> ΚΛΕΙΣΤΗ
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            Άνοιγμα: {isoLocal(shift.opened_at)}
            {shift.closed_at && <> · Κλείσιμο: {isoLocal(shift.closed_at)}</>}
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="p-8 text-center text-slate-500">
            <RefreshCw size={28} className="mx-auto animate-spin mb-2 text-indigo-500" />
            Φόρτωση αναφοράς...
          </div>
        )}

        {!loading && report && (
          <div className="space-y-5">
            {/* Cash totals (operator-level, NOT fiscal) */}
            <div>
              <div className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-2">
                Ταμείο
              </div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <Tile
                  label="Μετρητά Αρχής"
                  value={fmtEUR(totals.cash_start || 0)}
                  color="slate"
                  data-testid="tile-cash-start"
                />
                <Tile label="Σύνολο" value={fmtEUR(totals.total)} color="indigo" />
                <Tile label="Ταμείο ΑΑ" value={fmtEUR(totals.aa)} color="emerald" />
                <Tile label="Ταμείο ΒΒ" value={fmtEUR(totals.bb)} color="amber" />
                <Tile
                  label="Μετρητά στο Συρτάρι"
                  value={fmtEUR(totals.cash_drawer || 0)}
                  sub="Αρχής + ΑΑ μετρ. + ΒΒ"
                  color="sky"
                  data-testid="tile-cash-drawer"
                />
              </div>
              {/* AA breakdown by payment method — only non-zero rows
                  are returned by the backend. Helps the manager balance
                  the till per cash / card / cheque / on-credit. */}
              {(totals.aa_breakdown || []).length > 0 && (
                <div className="mt-3 border-2 border-emerald-200 rounded-md bg-emerald-50/40">
                  <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-bold text-emerald-800 bg-emerald-100/70 border-b border-emerald-200">
                    Ανάλυση ΑΑ ανά τρόπο πληρωμής
                  </div>
                  <ul className="divide-y divide-emerald-100 text-sm">
                    {(totals.aa_breakdown || []).map((b, i) => (
                      <li
                        key={b.payment_id ?? b.method_name}
                        className="flex items-center justify-between px-3 py-1.5"
                        data-testid={`aa-method-${i}`}
                      >
                        <span className="font-medium text-emerald-900">{b.method_name}</span>
                        <span className="font-mono font-bold text-emerald-800">{fmtEUR(b.amount)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Orders breakdown */}
            <div>
              <div className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-2">
                Παραγγελίες
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Tile
                  label="Σύνολο"
                  value={orders.total}
                  sub={fmtEUR(orders.total_amount)}
                  color="slate"
                />
                <Tile
                  label={<span className="flex items-center gap-1"><ShoppingBag size={11} /> Retail</span>}
                  value={orders.retail}
                  sub={fmtEUR(orders.retail_amount)}
                  color="sky"
                />
                <Tile
                  label={<span className="flex items-center gap-1"><UtensilsCrossed size={11} /> Εστίαση</span>}
                  value={orders.restaurant}
                  sub={fmtEUR(orders.restaurant_amount)}
                  color="fuchsia"
                />
                <Tile
                  label={<span className="flex items-center gap-1"><Bike size={11} /> Delivery</span>}
                  value={orders.delivery}
                  sub={fmtEUR(orders.delivery_amount)}
                  color="amber"
                />
              </div>
            </div>

            {/* Corrections & cancellations */}
            <div>
              <div className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-2">
                Διορθώσεις & Ακυρώσεις
              </div>
              <div className="grid grid-cols-3 gap-3">
                <Tile label="Διορθώσεις" value={corrections.count} sub="παραγγελιών" color="red" />
                <Tile label="Διαγραμμένα είδη" value={corrections.items_deleted} color="red" />
                <Tile label="Ακυρώσεις" value={corrections.cancellations_count} sub="αποδείξεων" color="red" />
              </div>
              {corrections.events?.length > 0 && (
                <div className="mt-3 border border-slate-200 rounded-md bg-slate-50 max-h-44 overflow-y-auto">
                  <ul className="divide-y divide-slate-200 text-sm">
                    {corrections.events.map((e, i) => (
                      <li key={i} className="px-3 py-1.5 flex items-center justify-between" data-testid={`correction-event-${i}`}>
                        <span className="text-slate-700">
                          <span className="text-xs text-slate-400 mr-2">{isoLocal(e.timestamp)}</span>
                          <b>{e.target_name}</b> — {e.summary}
                        </span>
                        <span className="font-mono text-red-700 font-bold whitespace-nowrap">
                          <XCircle size={11} className="inline mr-0.5" /> {e.deleted} είδ.
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Discounts */}
            <div>
              <div className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-2">
                Εκπτώσεις
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Tile
                  label={<span className="flex items-center gap-1"><Percent size={11} /> Σύνολο εκπτώσεων</span>}
                  value={fmtEUR(discounts.amount)}
                  color="amber"
                />
                <Tile label="Γραμμές με έκπτωση" value={discounts.lines} color="slate" />
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="border-t-2 border-slate-200 pt-3 mt-2 gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={onClose}
            className="border-2"
            data-testid="operator-report-close"
          >
            Επιστροφή
          </Button>
          <div className="flex-1" />
          <Button
            onClick={printSnapshot}
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold"
            data-testid="operator-report-x"
          >
            <Printer size={14} className="mr-1.5" />
            X — Στιγμιότυπο εκτύπωση
          </Button>
          {isOpen && canCloseShift && (
            <Button
              onClick={() => { printSnapshot(); onCloseShift?.(); }}
              disabled={loading}
              className="bg-red-600 hover:bg-red-700 text-white font-bold"
              data-testid="operator-report-z"
            >
              <FileBarChart2 size={14} className="mr-1.5" />
              Z — Εκτύπωση & Κλείσιμο Βάρδιας
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
